import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Location } from "history";
import { match as Match } from "react-router-dom";
import { injectIntl, IntlShape } from "react-intl";
import {
  EnumBookingFrom,
  EnumPaths,
  navTo,
} from "../../../../utils/navigation";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import { withTwoColLayout } from "../../../../utils";
import Table, {
  ALL_FILTER,
  ALL_FILTER_NUMBER,
  IFilterField,
} from "../../../../components/Table";
import { TableChangeParams } from "../../../../components/Table/types";
import { getTranslation } from "../../../../utils/translation";
import DropdownQuoteRequestStatus from "../../../../components/Tailwind/DropdownQuoteRequestStatus";
import { EnumFilterFields } from "../../../../utils/filter";
import { EnumPermissionEntity } from "../../../../lib/ground-aws-graphql-core/api/graphql/types";
import images from "../../../../images";
import contextStore from "../../../../redux/store";
import { frFRLang, getLocale } from "../../../../lang";
import { ILoadOptionsResponse } from "../../global-orders/global-orders-list/table";
import {
  DEFAULT_LIMIT,
  displayDayDDMMYYYY_HHMM,
} from "../../../../utils/config";
import {
  QuoteRequest,
  QuoteRequestService,
  SearchableQuoteRequestsFilterInput,
} from "../../../../lib/ground-aws-graphql-core/models/Api/QuoteRequests";
import history from "../../../../history";
import Dropdown, {
  DropdownValues,
} from "../../../../components/Tailwind/Dropdown";

interface Props {
  match: Match<{ cid: string }>;
  intl: IntlShape;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
    filters: IFilterField<{
      code: string;
      id: string;
      name: string;
    }>[];
  }>;
}

const ListQuoteRequests = (props: Props) => {
  const { intl, match, location } = props;
  const [loading, setLoading] = useState(false);
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const listQuoteRequests = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.listQuoteRequests
  );

  const quoteRequests = GroundGraphqlContextStore.useStoreState(
    state => state.quoteRequests.quoteRequests.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    state => state.quoteRequests.quoteRequests.total
  );

  const listQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.listQuoteRequestStatus
  );

  const quoteRequestStatuss = GroundGraphqlContextStore.useStoreState(
    state => state.quoteRequests.quoteRequestStatuss.items
  );

  const center = GroundGraphqlContextStore.useStoreState(
    state => state.center.center
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const locale = contextStore.useStoreState(state => state.settings.locale);
  const currentAppLocale = getLocale(locale);

  const lang = currentAppLocale.locale || frFRLang.locale;

  useEffect(() => {
    if (location?.state) {
      fetchData(
        location?.state?.pageIndex,
        true,
        location?.state?.limit,
        location?.state?.filters
      );
    } else fetchData();
  }, []);

  const fetchData = (
    pageIndex = 0,
    loader = true,
    limit = DEFAULT_LIMIT,
    filters?: IFilterField<any>[]
  ) => {
    const filter: SearchableQuoteRequestsFilterInput = {};
    if (filters?.length) {
      filters.forEach(f => {
        if (
          f.type === EnumFilterFields[EnumFilterFields.STATUS] &&
          f.value?.id !== undefined &&
          f.value.id !== ALL_FILTER
        ) {
          filter.status = { eq: f.value.id };
        }

        if (
          f.type === EnumFilterFields[EnumFilterFields.CLIENT_NAME] &&
          f.value
        ) {
          filter.lastname = { like: f.value };
        }
      });
    }

    if (loader) setLoading(true);

    Promise.all([
      listQuoteRequests({
        buildingId: match.params.cid,
        offset: pageIndex * limit,
        limit,
        filter,
      }),
      listQuoteRequestStatus(),
    ]).finally(() => setLoading(false));
  };

  const loadQuoteRequestStatus = async (
    _searchQuery: any,
    _loadedOptions: any,
    { page }: { page: number }
  ): Promise<ILoadOptionsResponse> => {
    const response = await listQuoteRequestStatus();
    let options = response.items.map(({ id, code, label }) => {
      return { id: code, code: id, name: getTranslation(label) };
    });

    options = [
      {
        id: ALL_FILTER,
        code: ALL_FILTER_NUMBER,
        name: intl.formatMessage({ id: "general.all" }),
      },
      ...options,
    ];

    return {
      options,
      hasMore: false,
      additional: {
        page: page + 1,
      },
    };
  };

  const onQuoteRequestClick = (id: number, editLink: string) => {
    history.push(editLink, initialParams);
  };

  const onCreateQuoteClick = (id: number, centerId: string) => {
    history.push(
      navTo(`${EnumPaths.BOOKINGS}/${EnumPaths.BOOK_AVAILABILITY}`, centerId),
      { from: EnumBookingFrom.QUOTE_LIST, quoteRequestId: id }
    );
  };

  const tableHead = [
    "general.ref",
    "page.list.quote.requests.table.head.external_ref",
    "general.client",
    "general.services",
    "general.created_at",
    "general.status",
    "page.list.quote.requests.table.head.validator",
    "general.updated_at",
    "page.list.quote.requests.table.head.quote",
    "general.actions",
  ];

  const tableBody = quoteRequests?.map((quoteRequest: QuoteRequest) => {
    const {
      id,
      priority,
      ref,
      external_ref,
      created_at: createdAt,
      updated_at: updatedAt,
      services,
      quotes,
      user,
      back_office_user,
      can_create_quote,
      status,
      next_status,
    } = quoteRequest;

    const editQuoteRequestLink = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.QUOTE_REQUESTS}/${id}/${EnumPaths.EDIT_MODE}`;

    const validator = `${back_office_user?.last_name || ""} ${
      back_office_user?.first_name || ""
    }`;

    const dropdownValues: DropdownValues[] = can_create_quote
      ? [
          {
            id: "create-quote",
            label: "page.list.quotes.create.quote",
            icon: images.edit,
            onClick: () => onCreateQuoteClick(id, match.params.cid),
          },
        ]
      : [];

    const quoteRequestElements = [
      {
        element: (
          <div className="flex flew-row items-center">
            <div>{ref}</div>
            {!!priority && (
              <img
                alt={`alert_${ref}`}
                src={images.highPriority}
                style={{ width: 25, height: 25 }}
                className="list-thumbnail responsive border-0 ml-2"
              />
            )}
          </div>
        ),
        onCellClick: () => onQuoteRequestClick(id, editQuoteRequestLink),
      },
      {
        element: external_ref,
        onCellClick: () => onQuoteRequestClick(id, editQuoteRequestLink),
      },
      {
        element: `${user.last_name} ${user.first_name}`,
        onCellClick: () => onQuoteRequestClick(id, editQuoteRequestLink),
      },
      {
        element: services
          .map((service: QuoteRequestService) => service.label[lang])
          .join(", "),
        onCellClick: () => onQuoteRequestClick(id, editQuoteRequestLink),
      },
      {
        element: displayDayDDMMYYYY_HHMM(createdAt, centerTimezone),
        onCellClick: () => onQuoteRequestClick(id, editQuoteRequestLink),
      },
      {
        element: (
          <DropdownQuoteRequestStatus
            quoteRequest={quoteRequest}
            quoteRequestStatus={quoteRequestStatuss.filter(s =>
              [...next_status, status.code].includes(s.code)
            )}
            intl={intl}
            className="items-center"
            fromQuoteRequestDetails={false}
            center={center}
          />
        ),
      },
      {
        element: validator,
        onCellClick: () => onQuoteRequestClick(id, editQuoteRequestLink),
      },
      {
        element: displayDayDDMMYYYY_HHMM(updatedAt, centerTimezone),
        onCellClick: () => onQuoteRequestClick(id, editQuoteRequestLink),
      },
      {
        element: (
          <div className="flex justify-center">
            {quotes && quotes?.length > 0 && (
              <img src={images.checked} alt="quote-ok" />
            )}
          </div>
        ),
        onCellClick: () => onQuoteRequestClick(id, editQuoteRequestLink),
      },
      {
        element: (
          <Dropdown
            values={dropdownValues}
            dataCy={`quote-requests-dropdown-${id}`}
          />
        ),
      },
    ];

    return {
      id: quoteRequest.id.toString(),
      rowElements: quoteRequestElements,
    };
  });

  const keyQuoteRequestStatus = EnumFilterFields[EnumFilterFields.STATUS];

  const loadOptions: {
    [key: string]: (
      searchQuery: any,
      _loadedOptions: any,
      { page }: { page: number }
    ) => Promise<ILoadOptionsResponse>;
  } = {};

  loadOptions[`${keyQuoteRequestStatus}`] = loadQuoteRequestStatus;

  const handleOnChange = (changeParams: TableChangeParams) => {
    const { pageIndex, limit, filters } = changeParams;

    // set initial params
    setInitialParams(changeParams);

    // fetch quote requests using filters
    fetchData(pageIndex, false, limit, filters);
  };

  return (
    <div>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: "page.list.quote.requests.document.title",
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.quote.requests.title"
        nbOfResults={total}
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        noDataText="page.list.quote.requests.empty"
        onChange={handleOnChange}
        limitChoiceEnabled
        paginationTotal={total}
        paginationLimit={DEFAULT_LIMIT}
        permissionEntity={EnumPermissionEntity.QUOTE_REQUEST}
        useFilterBlock
        filterBlockTitle="page.list.quote.requests.filter.title"
        loading={loading}
        setLoading={setLoading}
        loadOptions={loadOptions}
        className="px-8"
      />
    </div>
  );
};

export default withTwoColLayout(injectIntl(ListQuoteRequests));
